import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Pipeline {
  readonly pipelineId: string;
  readonly pipelineCode: string;
  readonly pipelineName: string;
  readonly sourceRef: string;
  readonly targetMart: string;
  readonly scheduleCron: string | null;
  readonly isEnabled: boolean;
}

interface Source {
  readonly sourceId: string;
  readonly sourceCode: string;
  readonly sourceName: string;
}

interface Mart {
  readonly martId: string;
  readonly martCode: string;
  readonly martName: string;
}

interface Run {
  readonly runId: string;
  readonly runType: string;
  readonly status: string;
  readonly rowsRead: string;
  readonly rowsWritten: string;
  readonly rowsRejected: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly failureReason: string | null;
}

/** M26 — ETL Monitoring Dashboard (§9, §31). Defines pipelines whose source is
 *  a registered data source and whose target is a catalogued mart, with their
 *  declarative steps and schedule (POST /v1/dwh/pipelines); runs them and
 *  shows each pipeline's run ledger — rows read / written / rejected, status
 *  and the error or reject reasons (GET /v1/dwh/pipelines/:id/runs). */
export function M26EltMonitoringDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<Pipeline[]>([]);
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState<Source[]>([]);
  const [marts, setMarts] = useState<Mart[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [pipelineName, setPipelineName] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [targetMart, setTargetMart] = useState('');
  const [scheduleCron, setScheduleCron] = useState('');
  const [steps, setSteps] = useState('');
  const [historyOf, setHistoryOf] = useState<Pipeline | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: Pipeline[]; total: number }>('/v1/dwh/pipelines', { token }),
      apiRequest<{ data: Source[] }>('/v1/dwh/sources', { token }),
      apiRequest<{ data: Mart[] }>('/v1/dwh/marts', { token }),
    ])
      .then(([p, s, m]) => {
        if (!alive) return;
        setRows(p.data); setTotal(p.total); setSources(s.data); setMarts(m.data); setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reloadKey]);

  const loadRuns = useCallback((p: Pipeline): void => {
    setHistoryOf(p);
    apiRequest<{ data: Run[] }>(`/v1/dwh/pipelines/${encodeURIComponent(p.pipelineId)}/runs`, { token })
      .then((r) => setRuns(r.data))
      .catch((e) => setError((e as Error).message));
  }, [token]);

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setNotice(null);
    setError(null);
    let transformJson: unknown;
    try {
      transformJson = JSON.parse(steps);
    } catch {
      setError(t('m26.elt.stepsInvalid'));
      return;
    }
    setBusy(true);
    try {
      await apiRequest('/v1/dwh/pipelines', {
        method: 'POST',
        token,
        body: { pipelineName, sourceRef, targetMart, scheduleCron: scheduleCron || undefined, transformJson },
      });
      setNotice(t('m26.elt.pipelineCreated'));
      setPipelineName(''); setSourceRef(''); setTargetMart(''); setScheduleCron(''); setSteps('');
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function run(p: Pipeline): Promise<void> {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const r = await apiRequest<{ data: Run }>(`/v1/dwh/pipelines/${encodeURIComponent(p.pipelineId)}/run`, { method: 'POST', body: {}, token });
      setNotice(t('m26.elt.runFinished', {
        code: p.pipelineCode,
        status: r.data.status,
        read: number(Number(r.data.rowsRead)),
        written: number(Number(r.data.rowsWritten)),
        rejected: number(Number(r.data.rowsRejected)),
      }));
      loadRuns(p);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="etl-monitoring-dashboard">
      <h1>{t('m26.elt.title')}</h1>
      <form aria-label="pipeline-create-form" onSubmit={onCreate}>
        <label htmlFor="pipeline-name">{t('m26.elt.pipelineNameLabel')}</label>
        <input id="pipeline-name" value={pipelineName} onChange={(e) => setPipelineName(e.target.value)} required />
        <label htmlFor="pipeline-source">{t('m26.elt.sourceRefLabel')}</label>
        <select id="pipeline-source" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} required>
          <option value="">{t('m26.elt.choose')}</option>
          {sources.map((s) => <option key={s.sourceId} value={s.sourceCode}>{`${s.sourceCode} — ${s.sourceName}`}</option>)}
        </select>
        <label htmlFor="pipeline-target">{t('m26.elt.targetMartLabel')}</label>
        <select id="pipeline-target" value={targetMart} onChange={(e) => setTargetMart(e.target.value)} required>
          <option value="">{t('m26.elt.choose')}</option>
          {marts.map((m) => <option key={m.martId} value={m.martCode}>{`${m.martCode} — ${m.martName}`}</option>)}
        </select>
        <label htmlFor="pipeline-schedule">{t('m26.elt.scheduleLabel')}</label>
        <input id="pipeline-schedule" value={scheduleCron} onChange={(e) => setScheduleCron(e.target.value)} />
        <label htmlFor="pipeline-steps">{t('m26.elt.stepsLabel')}</label>
        <textarea id="pipeline-steps" value={steps} onChange={(e) => setSteps(e.target.value)} rows={8} required />
        <button type="submit" disabled={busy}>{t('m26.elt.createPipeline')}</button>
      </form>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m26.elt.loading')}</p> : (
        <>
          <p aria-label="pipeline-count">{t('m26.elt.count', { count: total })}</p>
          <table aria-label="pipeline-table">
            <thead><tr><th>{t('m26.elt.colCode')}</th><th>{t('m26.elt.colName')}</th><th>{t('m26.elt.colSource')}</th><th>{t('m26.elt.colTarget')}</th><th>{t('m26.elt.colSchedule')}</th><th>{t('m26.elt.colStatus')}</th><th></th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.pipelineId}>
                <td>{p.pipelineCode}</td><td>{p.pipelineName}</td><td>{p.sourceRef}</td><td>{p.targetMart}</td><td>{p.scheduleCron ?? ''}</td>
                <td>{p.isEnabled ? t('m26.elt.enabled') : t('m26.elt.disabled')}</td>
                <td>
                  {p.isEnabled && <button type="button" aria-label={`run-${p.pipelineId}`} disabled={busy} onClick={() => { void run(p); }}>{t('m26.elt.run')}</button>}
                  <button type="button" aria-label={`history-${p.pipelineId}`} onClick={() => loadRuns(p)}>{t('m26.elt.history')}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}
      {historyOf && (
        <section aria-label="run-history">
          <h2>{t('m26.elt.historyTitle', { code: historyOf.pipelineCode })}</h2>
          <table aria-label="run-table">
            <thead><tr><th>{t('m26.elt.colStarted')}</th><th>{t('m26.elt.colRunType')}</th><th>{t('m26.elt.colStatus')}</th><th>{t('m26.elt.colRead')}</th><th>{t('m26.elt.colWritten')}</th><th>{t('m26.elt.colRejected')}</th><th>{t('m26.elt.colCompleted')}</th><th>{t('m26.elt.colError')}</th></tr></thead>
            <tbody>{runs.map((r) => (
              <tr key={r.runId}>
                <td>{at(r.startedAt)}</td><td>{r.runType}</td><td>{r.status}</td>
                <td>{number(Number(r.rowsRead))}</td><td>{number(Number(r.rowsWritten))}</td><td>{number(Number(r.rowsRejected))}</td>
                <td>{at(r.completedAt)}</td><td>{r.failureReason ?? ''}</td>
              </tr>
            ))}</tbody>
          </table>
        </section>
      )}
    </main>
  );
}
